/**
 * @fileoverview 安灯管理模块常量定义
 * 
 * @last-updated 2025-11-06
 */

import { 
  AndonType, 
  AndonProtocol, 
  AndonCallType, 
  AndonCallStatus, 
  AndonPriority,
  WorkstationMapStatus 
} from './enums'

/**
 * 安灯类型标签映射
 */
export const ANDON_TYPE_LABELS: Record<AndonType, string> = {
  [AndonType.BUTTON]: '按钮式',
  [AndonType.ROPE]: '拉绳式',
  [AndonType.TOUCH]: '触摸屏',
  [AndonType.TOWER]: '灯柱',
}

/**
 * 通信协议标签映射
 */
export const ANDON_PROTOCOL_LABELS: Record<AndonProtocol, string> = {
  [AndonProtocol.HTTP]: 'HTTP协议',
  [AndonProtocol.MQTT]: 'MQTT协议',
  [AndonProtocol.TCP]: 'TCP协议',
  [AndonProtocol.MODBUS]: 'Modbus协议',
}

/**
 * 呼叫类型标签映射
 */
export const ANDON_CALL_TYPE_LABELS: Record<AndonCallType, string> = {
  [AndonCallType.QUALITY]: '质量异常',
  [AndonCallType.EQUIPMENT]: '设备故障',
  [AndonCallType.MATERIAL]: '物料短缺',
  [AndonCallType.PROCESS]: '换型',
  [AndonCallType.SAFETY]: '安全事故',
  [AndonCallType.OTHER]: '其他异常',
}

/**
 * 呼叫类型颜色映射（用于前端显示）
 */
export const ANDON_CALL_TYPE_COLORS: Record<AndonCallType, string> = {
  [AndonCallType.QUALITY]: '#faad14',    // 黄色 - 质量异常
  [AndonCallType.EQUIPMENT]: '#f5222d',  // 红色 - 设备故障
  [AndonCallType.MATERIAL]: '#52c41a',   // 绿色 - 物料短缺
  [AndonCallType.PROCESS]: '#1890ff',    // 蓝色 - 换型
  [AndonCallType.SAFETY]: '#ff4d4f',     // 深红色 - 安全事故
  [AndonCallType.OTHER]: '#8c8c8c',      // 灰色 - 其他异常
}

/**
 * 呼叫状态标签映射
 */
export const ANDON_CALL_STATUS_LABELS: Record<AndonCallStatus, string> = {
  [AndonCallStatus.PENDING]: '待响应',
  [AndonCallStatus.RESPONDED]: '已响应',
  [AndonCallStatus.COMPLETED]: '已完成',
  [AndonCallStatus.CANCELLED]: '已取消',
}

/**
 * 呼叫状态颜色映射
 */
export const ANDON_CALL_STATUS_COLORS: Record<AndonCallStatus, string> = {
  [AndonCallStatus.PENDING]: '#ff4d4f',     // 红色 - 待响应
  [AndonCallStatus.RESPONDED]: '#faad14',   // 黄色 - 已响应
  [AndonCallStatus.COMPLETED]: '#52c41a',   // 绿色 - 已完成
  [AndonCallStatus.CANCELLED]: '#8c8c8c',   // 灰色 - 已取消
}

/**
 * 优先级标签映射
 */
export const ANDON_PRIORITY_LABELS: Record<AndonPriority, string> = {
  [AndonPriority.LOW]: '低',
  [AndonPriority.MEDIUM]: '中',
  [AndonPriority.HIGH]: '高',
  [AndonPriority.URGENT]: '紧急',
}

/**
 * 优先级颜色映射
 */
export const ANDON_PRIORITY_COLORS: Record<AndonPriority, string> = {
  [AndonPriority.LOW]: '#52c41a',      // 绿色 - 低
  [AndonPriority.MEDIUM]: '#1890ff',   // 蓝色 - 中
  [AndonPriority.HIGH]: '#faad14',     // 黄色 - 高
  [AndonPriority.URGENT]: '#f5222d',   // 红色 - 紧急
}

/**
 * 工位状态标签映射（实时面板）
 */
export const WORKSTATION_MAP_STATUS_LABELS: Record<WorkstationMapStatus, string> = {
  [WorkstationMapStatus.NORMAL]: '正常',
  [WorkstationMapStatus.ALERT]: '呼叫中',
  [WorkstationMapStatus.RESPONDED]: '响应中',
}

/**
 * 工位状态颜色映射（实时面板）
 */
export const WORKSTATION_MAP_STATUS_COLORS: Record<WorkstationMapStatus, string> = {
  [WorkstationMapStatus.NORMAL]: '#52c41a',      // 绿色 - 正常
  [WorkstationMapStatus.ALERT]: '#f5222d',       // 红色 - 呼叫中
  [WorkstationMapStatus.RESPONDED]: '#faad14',   // 黄色 - 响应中
}

/**
 * 默认超时阈值（分钟）
 */
export const DEFAULT_TIMEOUT_THRESHOLDS: Record<AndonCallType, number> = {
  [AndonCallType.QUALITY]: 5,       // 质量异常：5分钟
  [AndonCallType.EQUIPMENT]: 3,     // 设备故障：3分钟
  [AndonCallType.MATERIAL]: 10,     // 物料短缺：10分钟
  [AndonCallType.PROCESS]: 8,       // 换型：8分钟
  [AndonCallType.SAFETY]: 2,        // 安全事故：2分钟
  [AndonCallType.OTHER]: 15,        // 其他异常：15分钟
}

/**
 * 呼叫类型图标映射（用于前端显示）
 * 使用 Ant Design 图标名称
 */
export const ANDON_CALL_TYPE_ICONS: Record<AndonCallType, string> = {
  [AndonCallType.QUALITY]: 'CheckCircleOutlined',      // 质量异常
  [AndonCallType.EQUIPMENT]: 'ToolOutlined',           // 设备故障
  [AndonCallType.MATERIAL]: 'InboxOutlined',           // 物料短缺
  [AndonCallType.PROCESS]: 'SettingOutlined',          // 换型
  [AndonCallType.SAFETY]: 'WarningOutlined',           // 安全事故
  [AndonCallType.OTHER]: 'QuestionCircleOutlined',     // 其他异常
}

/**
 * 统计周期选项
 */
export const ANDON_STATS_PERIOD_OPTIONS = [
  { label: '7天', value: '7d' },
  { label: '30天', value: '30d' },
  { label: '24小时', value: '24h' },
] as const

/**
 * 导出类型选项
 */
export const ANDON_EXPORT_TYPE_OPTIONS = [
  { label: '呼叫记录', value: 'calls' },
  { label: '统计数据', value: 'statistics' },
] as const

/**
 * 导出格式选项
 */
export const ANDON_EXPORT_FORMAT_OPTIONS = [
  { label: 'Excel', value: 'excel' },
  { label: 'CSV', value: 'csv' },
] as const

/**
 * 最大照片数量
 */
export const MAX_ANDON_IMAGES = 3

/**
 * 最大照片大小（字节）
 */
export const MAX_ANDON_IMAGE_SIZE = 5 * 1024 * 1024  // 5MB

/**
 * 呼叫单号前缀
 */
export const ANDON_CALL_NO_PREFIX = 'AND'

/**
 * 呼叫单号位数
 */
export const ANDON_CALL_NO_DIGITS = 6

/**
 * 实时面板刷新间隔（秒）
 */
export const ANDON_DASHBOARD_REFRESH_INTERVAL = 5

/**
 * 实时面板最大告警数
 */
export const ANDON_DASHBOARD_MAX_ALERTS = 10


/**
 * OEE 损失类型标签映射
 */
export const ANDON_LOSS_TYPE_LABELS: Record<number, string> = {
  1: '设备故障',
  2: '换型调整',
  3: '空转短停',
  4: '速度降低',
  5: '启动废品',
  6: '生产废品',
}
